import { createAsyncThunk } from "@reduxjs/toolkit";
import { invalidateFreightResponse, updateSkipFreightCalculation } from "../../../checkout/shop-checkout";
import { fetchUserProfile } from "../../../user-profile/user-profile.thunks";
import { waitForCart, waitForUser } from "../../../../store/store";
import { ShopCartUrlContext } from "../../../../common/utils/cart-util";
import { StoreState } from "../../../../store/store.interface";
import { getCartContext, getGuestCartToken, getItemId, getLoggedInCartQuoteId } from "../store/shop-cart.selectors";
import { ShopCart } from "../interfaces/shop-cart.interface";
import { getCartItemMagento, getTokenFromMagento } from "./shop-cart-commons";
import {
  removeGuestCartItemsMagentoApiCall,
  removeItemFromGuestCartMagentoApiCall,
  removeItemFromLoggedInCartMagentoApiCall,
  removeLoggedInCartItemsMagentoApiCall,
} from "../api/shop-cart.api";
import { getPromoCode } from "../../../promo-code/promo-code.selectors";
import { getZipCode } from "../../../estimated-tax/estimated-tax.selectors";
import { getEstimatedTax } from "../../../estimated-tax/estimated-tax.thunks";
import { getCustomerToken, isCustomerLoginSuccess } from "../../../customer/customer";
import { deletePromoCode } from "./delete-promo-code.thunks";

export const removeFromCart = createAsyncThunk('shopCart/removeProduct', async (shopCartUrlContext: ShopCartUrlContext, thunkAPI) => {
    try {
      thunkAPI.dispatch(invalidateFreightResponse());
      thunkAPI.dispatch(updateSkipFreightCalculation(false));
      thunkAPI.dispatch(fetchUserProfile());
      await waitForUser();
      shopCartUrlContext.isLoggedIn = getCartContext(thunkAPI.getState() as StoreState).isLoggedIn;
      let cart: ShopCart;
      await getTokenFromMagento(thunkAPI);
      const state = thunkAPI.getState() as StoreState;
      const itemId = getItemId(
        state,
        shopCartUrlContext.addProductMagentoRequest?.cartItem.sku,
        shopCartUrlContext.addProductRequest?.productIdentifier
      );
      const promoCode = getPromoCode(state);
      await removeFromMagentoCart(state, itemId);

      // If we have already found a promo code, remove it after the quantity changes
      if (promoCode) {
        await thunkAPI.dispatch(deletePromoCode({ forceUpdate: true, skipCalculation: true }));
      }

      cart = await getCartItemMagento(state);
      return { ...cart, store: shopCartUrlContext.storeCode };
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    } finally {
      const state = thunkAPI.getState() as StoreState;
  
      // If we have already found an estimated tax, update it after the quantity changes
      const zipCode = getZipCode(state);
      if (zipCode) {
        thunkAPI.dispatch(getEstimatedTax({ zipCode }));
      }
    }
  }, {
    condition: (async (_arg) => {
      await waitForCart();
      return true;
    })
  });

export async function removeFromMagentoCart(state: StoreState, itemId, forceRemove = false) {
    if (isCustomerLoginSuccess(state)) {
      const cartQuoteId = getLoggedInCartQuoteId(state);
      const customerToken = getCustomerToken(state);
      if ((cartQuoteId || forceRemove) && itemId) {
        return await removeItemFromLoggedInCartMagentoApiCall(itemId, customerToken);
      } else {
        throw new Error(itemId ? 'quoteId not defined' : 'magento itemId not defined');
      }
    } else {
      const guestCartToken = getGuestCartToken(state);
      if (guestCartToken && itemId) {
        return await removeItemFromGuestCartMagentoApiCall(itemId, guestCartToken);
      } else {
        throw new Error(itemId ? 'magentoToken not defined' : 'magento itemId not defined');
      }
    }
  }

export async function removeAllItemsFromMagentoCart(state: StoreState) {
  if (isCustomerLoginSuccess(state)) {
    const customerToken = getCustomerToken(state);
    if (customerToken) {
      return await removeLoggedInCartItemsMagentoApiCall(customerToken);
    }
    throw new Error('customerToken not defined');
  }

  const guestCartToken = getGuestCartToken(state);
  if (guestCartToken) {
    return await removeGuestCartItemsMagentoApiCall(guestCartToken);
  }
  throw new Error('magentoToken not defined');
}
